<script setup lang="ts">
// UpdateToast — aviso discreto "Nueva versión disponible" (D77).
//
// Aparece solo cuando hay una versión nueva esperando Y el usuario no navegó
// en un rato (el camino feliz es invisible: la app se actualiza sola en el
// próximo cambio de sección — ver useAppUpdate.ts). Persistente (sin
// auto-hide), descartable con X; descartar NO cancela el update, que igual
// se aplica en la próxima navegación.
//
// Montado en App.vue junto a CookieBanner (fuera de RouterView: visible con
// y sin sesión). Patrón visual del toast de CredentialAlertBanner (cab-toast).
import { Icon } from '@/components/ui';
import { useAppUpdate } from '@/composables/useAppUpdate';

const { toastVisible, applyFromToast, dismissToast } = useAppUpdate();
</script>

<template>
  <Transition name="upd-toast">
    <div v-if="toastVisible" class="upd-toast" role="status" aria-live="polite">
      <div class="upd-body">
        <p class="upd-title">Hay una versión nueva de WiserVet</p>
        <p class="upd-text">Actualiza cuando quieras — no perderás lo que estás haciendo al navegar.</p>
      </div>
      <button type="button" class="upd-cta" @click="applyFromToast">Actualizar</button>
      <button type="button" class="upd-close" aria-label="Ocultar aviso" @click="dismissToast">
        <Icon name="x" :size="16" />
      </button>
    </div>
  </Transition>
</template>

<style scoped>
.upd-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 210; /* sobre el toast de recuperación (200) */
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 420px;
  padding: 12px 12px 12px 20px;
  background: var(--navy-900, #064079);
  color: #fff;
  border-radius: 10px;
  font-size: 14px;
  box-shadow: 0 4px 16px rgba(6, 64, 121, 0.35);
}

.upd-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.upd-title {
  margin: 0;
  font-weight: 600;
}

.upd-text {
  margin: 0;
  font-size: 12.5px;
  opacity: 0.85;
}

/* CTA: blanco sobre navy ≈ 9:1, 44px touch target */
.upd-cta {
  flex-shrink: 0;
  min-height: 44px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: #fff;
  color: var(--navy-900, #064079);
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.15s;
}
.upd-cta:hover {
  opacity: 0.9;
}
.upd-cta:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.upd-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
}
.upd-close:hover {
  background: rgba(255, 255, 255, 0.12);
}
.upd-close:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.upd-toast-enter-active,
.upd-toast-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.upd-toast-enter-from,
.upd-toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .upd-toast-enter-active,
  .upd-toast-leave-active {
    transition: none;
  }
}

@media (max-width: 640px) {
  .upd-toast {
    left: 12px;
    right: 12px;
    bottom: 12px;
    max-width: none;
    flex-wrap: wrap;
  }
}
</style>
